import React from "react";
import {
  TableRow,
  TableCell,
  IconButton,
  TextField,
  Typography,
  Table,
  TableHead,
  TableBody,
  Tooltip,
  Paper,
  TableContainer,
  Box,
  FormControl,
  Select,
  MenuItem,
  SelectChangeEvent,
  useTheme,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import { CompanyIdIssuanceInfo, Status } from "IF/interfaceType";
import { convertNumberToFullWidthDigits } from "Utils/format";

interface CompanyInfoRowProps {
  data: CompanyIdIssuanceInfo[];
  editIndex: number | null;
  editFormData: CompanyIdIssuanceInfo;
  onEdit: (index: number) => void;
  handleChange: (
    event:
      | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | SelectChangeEvent<string>,
    child?: React.ReactNode
  ) => void;
  handleUpdate: () => void;
  handleCancel: () => void;
  handleDelete: (index: number) => void;
  handleConfirmModalOpen: (message: string, confirmAction: () => void) => void;
  handleTextChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  tableBodyHeight: number;
}

const CompanyIdIssuanceTable: React.FC<CompanyInfoRowProps> = ({
  data,
  editIndex,
  editFormData,
  onEdit,
  handleChange,
  handleUpdate,
  handleCancel,
  handleDelete,
  handleConfirmModalOpen,
  handleTextChange,
  tableBodyHeight,
}) => {
  // 各列の幅割合
  const columnWidths: Record<string, string> = {
    companyName: "15%",
    companyCode: "15%",
    status: "15%",
    action: "10%",
  };

  const theme = useTheme();

  return (
    <>
      <Box>
        <Typography
          fontWeight="fontWeightBold"
          sx={{
            display: "inline-block",
            borderBottom: "5px solid #FF8C00",
            paddingBottom: "0.5px",
            color: "text.primary",
          }}
        >
          検索結果：{convertNumberToFullWidthDigits(data.length)}件
        </Typography>
      </Box>
      <TableContainer
        component={Paper}
        sx={{
          minHeight: tableBodyHeight,
          maxHeight: tableBodyHeight,
          overflowY: "auto",
          backgroundColor: theme.palette.custom.row,
        }}
      >
        <Table
          sx={{
            "& th, & td": {
              textAlign: "center",
              verticalAlign: "middle",
            },
            "& th": {
              color: "#fff",
            },
          }}
        >
          <TableHead
            sx={{
              position: "sticky",
              top: 0,
              backgroundColor: theme.palette.custom.tableHeader,
              zIndex: 1,
            }}
          >
            <TableRow
              sx={{
                height: 52,
              }}
            >
              <TableCell
                rowSpan={2}
                sx={{
                  padding: "2px 4px",
                  fontWeight: "bold",
                  fontSize: "0.875rem",
                  verticalAlign: "middle",
                  width: columnWidths.companyCode,
                }}
              >
                企業コード
              </TableCell>
              <TableCell
                rowSpan={2}
                sx={{
                  padding: "2px 4px",
                  fontWeight: "bold",
                  fontSize: "0.875rem",
                  verticalAlign: "middle",
                  width: columnWidths.companyName,
                }}
              >
                企業名
              </TableCell>
              <TableCell
                rowSpan={2}
                sx={{
                  padding: "2px 4px",
                  fontWeight: "bold",
                  fontSize: "0.875rem",
                  verticalAlign: "middle",
                  width: columnWidths.action,
                }}
              >
                ステータス
              </TableCell>
              <TableCell
                rowSpan={1}
                colSpan={2}
                sx={{
                  padding: "2px 4px",
                  fontWeight: "bold",
                  fontSize: "0.875rem",
                  verticalAlign: "middle",
                  width: columnWidths.action,
                }}
              >
                配信可能人数
              </TableCell>
              <TableCell
                rowSpan={2}
                sx={{
                  padding: "2px 4px",
                  fontWeight: "bold",
                  fontSize: "0.875rem",
                  verticalAlign: "middle",
                  width: columnWidths.action,
                }}
              >
                アクション
              </TableCell>
            </TableRow>
            <TableRow
              sx={{
                height: 52,
              }}
            >
              <TableCell
                rowSpan={1}
                colSpan={1}
                sx={{
                  padding: "2px 4px",
                  fontWeight: "bold",
                  fontSize: "0.875rem",
                  verticalAlign: "middle",
                  width: columnWidths.action,
                }}
              >
                メール
              </TableCell>
              <TableCell
                rowSpan={1}
                colSpan={1}
                sx={{
                  padding: "2px 4px",
                  fontWeight: "bold",
                  fontSize: "0.875rem",
                  verticalAlign: "middle",
                  width: columnWidths.action,
                }}
              >
                SMS
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((row, index) => {
              // 編集モード
              return (
                <TableRow
                  key={index}
                  sx={{
                    height: 84,
                  }}
                >
                  <TableCell
                    sx={{
                      padding: "2px 4px",
                      verticalAlign: "middle",
                    }}
                  >
                    <Typography variant="body2" sx={{ fontSize: "0.875rem" }}>
                      {row.companyCode}
                    </Typography>
                  </TableCell>
                  <TableCell
                    sx={{
                      padding: "2px 4px",
                      verticalAlign: "middle",
                    }}
                  >
                    <Typography variant="body2" sx={{ fontSize: "0.875rem" }}>
                      {row.companyName}
                    </Typography>
                  </TableCell>
                  {/* ステータス */}
                  <TableCell sx={{ padding: "2px 4px" }}>
                    {editIndex === index && row.status !== Status.Inactive ? (
                      <FormControl fullWidth variant="outlined" size="small">
                        <Select
                          name="status"
                          value={editFormData.status}
                          onChange={handleChange}
                        >
                          <MenuItem value={Status.Active}>利用中</MenuItem>
                          <MenuItem value={Status.Stop}>停止中</MenuItem>
                        </Select>
                      </FormControl>
                    ) : (
                      <Typography
                        variant="body2"
                        color={row.status === Status.Stop ? "red" : "green"}
                      >
                        {row.status === Status.Active
                          ? "利用中"
                          : row.status === Status.Inactive
                            ? "利用なし"
                            : row.status === Status.Stop
                              ? "停止中"
                              : ""}
                      </Typography>
                    )}
                  </TableCell>
                  {/* メール制限 */}
                  <TableCell sx={{ padding: "2px 4px" }}>
                    {editIndex === index ? (
                      <TextField
                        size="small"
                        type="number"
                        name="mailSendLimit"
                        value={editFormData.mailSendLimit}
                        onChange={handleTextChange}
                        inputProps={{ maxLength: 255, min: 0 }}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                      />
                    ) : (
                      <TextField
                        size="small"
                        type="number"
                        name="mailSendLimit"
                        disabled
                        value={row.mailSendLimit}
                        inputProps={{ maxLength: 255, min: 0 }}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                      />
                    )}
                  </TableCell>
                  {/* SMS制限 */}
                  <TableCell sx={{ padding: "2px 4px" }}>
                    {editIndex === index ? (
                      <TextField
                        size="small"
                        type="number"
                        name="smsSendLimit"
                        value={editFormData.smsSendLimit}
                        onChange={handleTextChange}
                        inputProps={{ maxLength: 255, min: 0 }}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                      />
                    ) : (
                      <TextField
                        size="small"
                        type="number"
                        name="smsSendLimit"
                        disabled
                        value={row.smsSendLimit}
                        inputProps={{ maxLength: 255, min: 0 }}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                      />
                    )}
                  </TableCell>
                  <TableCell
                    sx={{
                      padding: "2px 4px",
                      verticalAlign: "middle",
                    }}
                  >
                    {editIndex === index ? (
                      <>
                        <Tooltip title={"保存"} placement="top" arrow>
                          <IconButton onClick={handleUpdate}>
                            <SaveIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={"キャンセル"} placement="top" arrow>
                          <IconButton onClick={handleCancel}>
                            <CancelIcon />
                          </IconButton>
                        </Tooltip>
                      </>
                    ) : (
                      <>
                        <Tooltip title={"編集"} placement="top" arrow>
                          <IconButton onClick={() => onEdit(index)}>
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={"削除"} placement="top" arrow>
                          <IconButton
                            onClick={() =>
                              handleConfirmModalOpen(
                                "選択した企業情報を削除します。よろしいですか？",
                                () => handleDelete(index)
                              )
                            }
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

export default CompanyIdIssuanceTable;
